import { useState } from "react";
import { Link } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import type { ScannedStatement, ScannedTransaction } from "@shared/schema";
import { CATEGORY_SLUGS } from '@shared/constants/categories';
import { SECTION_SLUGS } from '@shared/constants/sections';

const CATEGORY_LABELS: Record<string, string> = {
  utility: "Utility",
  subscription: "Subscription",
  insurance: "Insurance",
  mortgage: "Mortgage",
  rent: "Rent",
  loan_payment: "Loan",
  investment: "Investment",
  government: "Government",
  other: "Other",
};

const CATEGORY_VALUES = ["utility", "subscription", "insurance", "mortgage", "rent", "loan_payment", "investment", "government", "other"] as const;
const FREQUENCY_VALUES = ["monthly", "biweekly", "weekly", "quarterly", "annual", "one-time"] as const;
const FREQUENCY_LABELS: Record<string, string> = {
  monthly: "Monthly",
  biweekly: "Biweekly",
  weekly: "Weekly",
  quarterly: "Quarterly",
  annual: "Annual",
  "one-time": "One-time",
};

const TARGET_SECTIONS: { value: string; label: string; categorySlug: string }[] = [
  { value: SECTION_SLUGS.FINANCIAL_AND_INSURANCE_PRE_AUTHORIZED_DEBITS, label: "Pre-authorized Debits", categorySlug: CATEGORY_SLUGS.FINANCIAL_AND_INSURANCE },
];

interface Props {
  clientId: string;
  statement: ScannedStatement;
  transactions: ScannedTransaction[];
  onDone?: () => void;
  showAllDefault?: boolean;
  mode?: "planner" | "executor";
  /**
   * Replaces the single-statement header (s217). A BATCH view shows transactions drawn
   * from several statements, so naming one institution and one account at the top would
   * be a straightforward lie about where the rows came from.
   */
  heading?: string;
}

export function StatementScanResults({ clientId, statement, transactions, onDone, showAllDefault = false, mode = "planner", heading }: Props) {
  const { toast } = useToast();
  const recurring = transactions.filter(t => t.isRecurring);
  const nonRecurring = transactions.filter(t => !t.isRecurring);

  const [showAll, setShowAll] = useState(showAllDefault);
  // Pre-check recurring+pending; non-recurring start unchecked
  const [checked, setChecked] = useState<Set<string>>(
    new Set(recurring.filter(t => t.status === "pending").map(t => t.id))
  );
  const [targetSection, setTargetSection] = useState(TARGET_SECTIONS[0].value);
  const [addedCount, setAddedCount] = useState<number | null>(null);
  const [mergedCount, setMergedCount] = useState(0);
  // Local overrides for frequency and category while the user edits
  const [localFrequencies, setLocalFrequencies] = useState<Map<string, string>>(new Map());
  const [localCategories, setLocalCategories] = useState<Map<string, string>>(new Map());

  const displayed = showAll ? transactions : recurring;

  const toggleChecked = (id: string) => {
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const patchMutation = useMutation({
    mutationFn: async ({ transactionId, patch }: { transactionId: string; patch: Record<string, string> }) => {
      const res = await apiRequest("PATCH", `/api/clients/${clientId}/scanned-transactions/${transactionId}`, patch);
      return res.json();
    },
    onError: (error: any) => {
      toast({ title: "Error saving", description: error.message, variant: "destructive" });
    },
  });

  const handleFrequencyChange = (transactionId: string, value: string) => {
    setLocalFrequencies(prev => new Map(prev).set(transactionId, value));
    patchMutation.mutate({ transactionId, patch: { frequency: value } });
  };

  const handleCategoryChange = (transactionId: string, value: string) => {
    setLocalCategories(prev => new Map(prev).set(transactionId, value));
    patchMutation.mutate({ transactionId, patch: { category: value } });
  };

  const bulkAddMutation = useMutation({
    mutationFn: async (transactionIds: string[]) => {
      const res = await apiRequest("POST", `/api/clients/${clientId}/scanned-transactions/bulk-add`, {
        transactionIds,
        targetSectionSlug: targetSection,
      });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "scanned-statements"] });
      setAddedCount(data.addedCount);
      setMergedCount(data.mergedCount ?? 0);
      setChecked(new Set());
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const periodLabel = statement.statementPeriodStart && statement.statementPeriodEnd
    ? `${statement.statementPeriodStart} – ${statement.statementPeriodEnd}`
    : null;

  const pendingRecurringCount = recurring.filter(t => t.status === "pending").length;
  const targetSectionEntry = TARGET_SECTIONS.find(s => s.value === targetSection);
  const targetSectionLabel = targetSectionEntry?.label ?? "your plan";
  const targetCategorySlug = targetSectionEntry?.categorySlug ?? "";

  const instructionText = mode === "executor"
    ? `We found ${recurring.length} recurring charge${recurring.length !== 1 ? "s" : ""}. Use the action buttons to mark each obligation as cancelled, transferred, or ongoing. This helps track what needs to be handled during estate settlement.`
    : `We found ${recurring.length} recurring charge${recurring.length !== 1 ? "s" : ""} in your statement. Select the ones you want to add to your estate plan, then click "Add to my plan" below. Uncheck any you want to skip.`;

  return (
    <div className="space-y-4">
      <div>
        {heading ? (
          <h3 className="font-semibold text-base">{heading}</h3>
        ) : (
          <>
            <h3 className="font-semibold text-base">
              {statement.institutionName ?? "Statement"}
              {statement.accountLast4 && <span className="text-muted-foreground font-normal ml-1">···{statement.accountLast4}</span>}
            </h3>
            {periodLabel && <p className="text-xs text-muted-foreground">{periodLabel}</p>}
          </>
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        {instructionText}
        {nonRecurring.length > 0 && !showAll && (
          <> {" "}<button className="underline text-primary" onClick={() => setShowAll(true)}>Show all {transactions.length} transactions</button></>
        )}
        {showAll && nonRecurring.length > 0 && (
          <> {" "}<button className="underline text-primary" onClick={() => setShowAll(false)}>Show recurring only</button></>
        )}
      </p>

      {displayed.length === 0 && (
        <p className="text-sm text-muted-foreground italic">No transactions to show.</p>
      )}

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30">
              <th className="w-8 px-3 py-2"></th>
              <th className="text-left px-3 py-2 font-medium">Payee</th>
              <th className="text-right px-3 py-2 font-medium">Amount</th>
              <th className="text-left px-3 py-2 font-medium hidden sm:table-cell">Frequency</th>
              <th className="text-left px-3 py-2 font-medium hidden md:table-cell">Category</th>
              <th className="text-left px-3 py-2 font-medium hidden md:table-cell">Status</th>
            </tr>
          </thead>
          <tbody>
            {displayed.map((t) => {
              const effectiveFrequency = localFrequencies.get(t.id) ?? t.frequency ?? "";
              const effectiveCategory = localCategories.get(t.id) ?? t.category;
              const isAdded = t.status === "added";
              return (
                <tr key={t.id} className={`border-b last:border-0 ${isAdded ? "opacity-50" : ""}`}>
                  <td className="px-3 py-2">
                    {isAdded ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      t.status === "pending" && (
                        <Checkbox
                          checked={checked.has(t.id)}
                          onCheckedChange={() => toggleChecked(t.id)}
                        />
                      )
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className="font-medium">{t.cleanName}</span>
                    <span className="block text-xs text-muted-foreground truncate max-w-[180px]">{t.description}</span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">${parseFloat(t.amount).toFixed(2)}</td>
                  <td className="px-3 py-2 hidden sm:table-cell">
                    <Select value={effectiveFrequency} onValueChange={(v) => handleFrequencyChange(t.id, v)}>
                      <SelectTrigger className="h-7 text-xs w-[110px] border-0 shadow-none px-1">
                        <SelectValue placeholder="Select…" />
                      </SelectTrigger>
                      <SelectContent>
                        {FREQUENCY_VALUES.map(f => (
                          <SelectItem key={f} value={f}>{FREQUENCY_LABELS[f]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2 hidden md:table-cell">
                    <Select value={effectiveCategory} onValueChange={(v) => handleCategoryChange(t.id, v)}>
                      <SelectTrigger className="h-7 text-xs w-[120px] border-0 shadow-none px-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORY_VALUES.map(c => (
                          <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2 hidden md:table-cell text-xs text-muted-foreground capitalize">{t.status}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Success state after bulk-add */}
      {addedCount !== null && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-200">
          <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
          <p className="text-sm text-green-800 flex-1">
            {/* s217: a matched payee FILLS an existing row rather than creating one, so
                "Added N" alone would overstate what happened. The server returns the split. */}
            Added {addedCount} item{addedCount !== 1 ? "s" : ""} to {targetSectionLabel}
            {mergedCount > 0 && ` (${mergedCount} matched a row you already had)`}.{" "}
            {targetCategorySlug && (
              <Link href={`/category/${targetCategorySlug}`} className="underline text-green-700 font-medium">
                View them <ArrowRight className="h-3.5 w-3.5 inline-block align-[-2px]" aria-hidden="true" />
              </Link>
            )}
          </p>
          <Button size="sm" variant="outline" onClick={onDone}>Done</Button>
        </div>
      )}

      {/* Add to plan controls — only shown in planner mode when there are unclaimed recurring items */}
      {mode === "planner" && addedCount === null && pendingRecurringCount > 0 && (
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Add to:</span>
            <Select value={targetSection} onValueChange={setTargetSection}>
              <SelectTrigger className="h-8 text-sm w-[220px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TARGET_SECTIONS.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={() => bulkAddMutation.mutate(Array.from(checked))}
            disabled={checked.size === 0 || bulkAddMutation.isPending}
          >
            {bulkAddMutation.isPending ? "Adding..." : `Add ${checked.size} selected item${checked.size !== 1 ? "s" : ""} to my plan`}
          </Button>
          <button
            className="text-sm text-muted-foreground underline"
            onClick={onDone}
          >
            Skip
          </button>
        </div>
      )}
    </div>
  );
}
